import { Fragment, useEffect, useMemo, useState } from 'react';
import type { StackSummary } from '@hub/shared';
import { api } from '../../../api/client';
import {
  ActionButton, Alert, CodeEditor, Collapsible, EmptyRow, Loading, StateDot, useFlash,
} from '../../../components/ui';
import {
  DOCKER_BASE, DataTable, Field, FormActions, TabToolbar, enc, errMsg,
  truncStyle, useTabResource, useToggleSet, type DockerCache,
} from './shared';

/** `up` runs after the file is written, so a compose failure comes back in the body. */
interface StackWriteResult { upError?: string }

function stackStatus(s: StackSummary): string {
  if (!s.services.length) return 'down';
  const running = s.services.filter((x) => x.state === 'running').length;
  return `running ${running}/${s.services.length}`;
}

export function StacksTab({ cache, refreshToken }: { cache: DockerCache; refreshToken: number }) {
  const { data, loading, error, reload } = useTabResource<StackSummary[], { stacks: StackSummary[] }>({
    cache, tab: 'stacks', path: `${DOCKER_BASE}/stacks`, extract: (r) => r.stacks, refreshToken,
  });
  const { show, node: flashNode } = useFlash();
  const [open, toggleOpen] = useToggleSet();
  const [formOpen, setFormOpen] = useState(false);

  const list = useMemo(() => data ?? [], [data]);

  const run = async (fn: () => Promise<unknown>) => {
    try {
      await fn();
    } catch (err) {
      show(errMsg(err), 'error');
      throw err;
    }
    await reload(true);
  };

  const toolbar = (
    <TabToolbar onRefresh={() => { void reload(true); }} flash={flashNode}>
      <CreateStackForm open={formOpen} onToggle={() => setFormOpen((v) => !v)} onCreated={() => reload(true)} />
    </TabToolbar>
  );

  if (loading && !data) return <>{toolbar}<Loading /></>;
  if (error && !data) return <>{toolbar}<Alert tone="error">{error}</Alert></>;

  return (
    <>
      {toolbar}
      <DataTable headers={['Name', 'Status', 'Services', 'Source', 'Path', 'Actions']}>
        {list.length === 0 && <EmptyRow colSpan={6}>No compose stacks.</EmptyRow>}
        {list.map((s) => {
          const isHub = s.source === 'hub';
          return (
            <Fragment key={s.name}>
              <tr>
                <td className="mono" style={{ color: 'var(--ha-text-primary)' }}>
                  {s.name}
                  {!s.manageable && (
                    <> <span
                      className="pill pill-muted"
                      title={s.source === 'instance'
                        ? 'Owned by a service instance — manage it from that service\'s page'
                        : 'Compose files are not readable by the hub'}
                    >{s.source === 'instance' ? 'service-owned' : 'listing only'}</span></>
                  )}
                </td>
                <td className="muted">{stackStatus(s)}</td>
                <td className="muted">{s.services.length}</td>
                <td><span className={`pill ${isHub ? 'pill-emerald' : 'pill-muted'}`}>{s.source}</span></td>
                <td className="mono muted" style={truncStyle('20rem')}>{s.workingDir || '–'}</td>
                <td style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                  {s.manageable && (
                    <>
                      <ActionButton onClick={() => run(() => api.post(`${DOCKER_BASE}/stacks/${enc(s.name)}/up`))}>Up</ActionButton>
                      <ActionButton
                        confirm={`Bring stack "${s.name}" down? Its containers will be stopped and removed.`}
                        onClick={() => run(() => api.post(`${DOCKER_BASE}/stacks/${enc(s.name)}/down`))}
                      >Down</ActionButton>
                      <ActionButton onClick={() => run(() => api.post(`${DOCKER_BASE}/stacks/${enc(s.name)}/restart`))}>Restart</ActionButton>
                    </>
                  )}
                  <button type="button" className="btn-ghost btn-sm" onClick={() => toggleOpen(s.name)}>
                    {isHub ? 'Edit' : 'View'}
                  </button>
                  {isHub && (
                    <ActionButton
                      danger
                      busyLabel="Deleting…"
                      confirm={`Delete stack "${s.name}"? Its containers are removed and the compose file is deleted.`}
                      onClick={() => run(() => api.delete(`${DOCKER_BASE}/stacks/${enc(s.name)}`))}
                    >Delete</ActionButton>
                  )}
                </td>
              </tr>
              {open.has(s.name) && (
                <tr>
                  <td colSpan={6} style={{ padding: '0 0.4rem 0.75rem' }}>
                    <StackPanel name={s.name} onSaved={() => reload(true)} />
                  </td>
                </tr>
              )}
            </Fragment>
          );
        })}
      </DataTable>
    </>
  );
}

function StackPanel({ name, onSaved }: { name: string; onSaved: () => Promise<void> }) {
  const [stack, setStack] = useState<StackSummary | null>(null);
  const [yaml, setYaml] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { show, node } = useFlash();

  useEffect(() => {
    let cancelled = false;
    api.get<{ stack: StackSummary }>(`${DOCKER_BASE}/stacks/${enc(name)}`)
      .then((r) => {
        if (cancelled) return;
        setStack(r.stack);
        setYaml(r.stack.composeYaml ?? '');
      })
      .catch((err: unknown) => { if (!cancelled) setError(errMsg(err)); });
    return () => { cancelled = true; };
  }, [name]);

  if (error) return <Alert tone="error">{error}</Alert>;
  if (!stack) return <Loading />;

  const editable = stack.source === 'hub';

  return (
    <div style={{
      background: 'var(--ha-bg-base)', border: '1px solid var(--ha-border)',
      borderRadius: '0.375rem', padding: '0.75rem',
    }}>
      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 0.75rem' }}>
        {stack.services.length === 0 && (
          <li className="muted" style={{ fontSize: '0.78rem' }}>No running services.</li>
        )}
        {stack.services.map((svc) => (
          <li key={svc.name} style={{ padding: '0.2rem 0', color: 'var(--ha-text-muted)', fontSize: '0.78rem' }}>
            <span className="mono" style={{ color: 'var(--ha-text-body)' }}>{svc.name}</span>{' '}
            <StateDot state={svc.state} />
          </li>
        ))}
      </ul>

      {stack.composeYaml === undefined ? (
        <p className="muted" style={{ fontSize: '0.78rem' }}>Compose file is not readable by the hub.</p>
      ) : editable ? (
        <>
          <CodeEditor value={yaml} onChange={setYaml} rows={14} />
          <FormActions>
            <ActionButton
              className="btn-primary"
              busyLabel="Deploying…"
              onClick={async () => {
                try {
                  const r = await api.put<StackWriteResult>(
                    `${DOCKER_BASE}/stacks/${enc(stack.name)}`, { composeYaml: yaml, up: true },
                  );
                  if (r.upError) throw new Error(r.upError);
                } catch (err) {
                  show(errMsg(err), 'error');
                  throw err;
                }
                await onSaved();
              }}
            >Save &amp; redeploy</ActionButton>
            {node}
          </FormActions>
        </>
      ) : (
        <>
          <pre className="output" style={{ maxHeight: '20rem' }}>{stack.composeYaml}</pre>
          <p className="muted" style={{ fontSize: '0.75rem', marginTop: '0.4rem' }}>
            Discovered project — read-only in the hub.
          </p>
        </>
      )}
    </div>
  );
}

function CreateStackForm({ open, onToggle, onCreated }: {
  open: boolean;
  onToggle: () => void;
  onCreated: () => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [yaml, setYaml] = useState('');
  const { show, node } = useFlash();

  const submit = async () => {
    if (!name.trim() || !yaml.trim()) {
      show('Name and compose YAML are required', 'error');
      throw new Error('Name and compose YAML are required');
    }
    try {
      const r = await api.post<StackWriteResult>(
        `${DOCKER_BASE}/stacks`, { name: name.trim(), composeYaml: yaml, up: true },
      );
      if (r.upError) throw new Error(r.upError);
    } catch (err) {
      show(errMsg(err), 'error');
      throw err;
    }
    await onCreated();
  };

  return (
    <Collapsible label="＋ Create stack" open={open} onToggle={onToggle}>
      <div style={{ marginBottom: '0.5rem' }}>
        <Field label="Stack name">
          <input className="form-input" placeholder="my-stack" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
      </div>
      <Field label="docker-compose.yml">
        <CodeEditor value={yaml} onChange={setYaml} rows={16} />
      </Field>
      <FormActions>
        <ActionButton className="btn-primary" busyLabel="Deploying…" onClick={submit}>Create &amp; deploy</ActionButton>
        {node}
      </FormActions>
    </Collapsible>
  );
}
